/**
 * User Profile Component for NumFree
 * Manages user profile data and settings
 *
 * Plan/pricing definitions: ../../NumFree/PRICING-TRUTH.md (server: llm-config.service.js DEFAULT_PLANS)
 */

import React, { useState, useEffect } from 'react';
import { authFetch } from '../auth';
import './UserProfile.css';

const UserProfile = ({ user, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [pwData, setPwData] = useState({ current: '', newPw: '', confirm: '' });
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    targetnumber1: user?.targetnumber1 || '',
    targetnumber2: user?.targetnumber2 || '',
    activetargetnum1Enabled: user?.activetargetnum1Enabled || false,
    activetargetnum2Enabled: user?.activetargetnum2Enabled || false,
    call_routing_priority: user?.call_routing_priority || 'simultaneous',
    simccid: user?.simccid || ''
  });
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch('/auth0/app_profile/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('nf_token')}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setIsEditing(false);
        if (onUpdate) {
          onUpdate();
        }
        alert('Profile updated successfully!');
      } else {
        throw new Error('Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      targetnumber1: user?.targetnumber1 || '',
      targetnumber2: user?.targetnumber2 || '',
      activetargetnum1Enabled: user?.activetargetnum1Enabled || false,
      activetargetnum2Enabled: user?.activetargetnum2Enabled || false,
      call_routing_priority: user?.call_routing_priority || 'simultaneous',
      simccid: user?.simccid || ''
    });
    setIsEditing(false);
  };

  if (!user) {
    return (
      <div className="user-profile">
        <div className="loading">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="user-profile">
      <div className="profile-header">
        <h3>Profile Information</h3>
        {!isEditing && (
          <button 
            onClick={() => setIsEditing(true)}
            className="edit-btn"
          >
            Edit Profile
          </button>
        )}
      </div>

      <div className="profile-content">
        {/* Account Information */}
        <div className="profile-section">
          <h4>Account Information</h4>
          <div className="profile-field">
            <label>Email:</label>
            <span>{user.email}</span>
          </div>
          <div className="profile-field">
            <label>Plan:</label>
            <span className="plan-badge">{user.plan || 'Free'}</span>
          </div>
          {user.freenumbers && user.freenumbers.length > 0 && (
            <div className="profile-field">
              <label>Your Numbers:</label>
              <div className="numbers-list">
                {user.freenumbers.map((number, index) => (
                  <span key={index} className="number-badge">{number}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Personal Information */}
        <div className="profile-section">
          <h4>Personal Information</h4>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-field">
                <label>First Name:</label>
                {isEditing ? (
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                  />
                ) : (
                  <span>{user.firstName || 'Not set'}</span>
                )}
              </div>
              <div className="form-field">
                <label>Last Name:</label>
                {isEditing ? (
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                  />
                ) : (
                  <span>{user.lastName || 'Not set'}</span>
                )}
              </div>
            </div>

            {/* Call Forwarding Settings */}
            <div className="profile-section">
              <h4>Call Forwarding</h4>
              <div className="form-field">
                <label>Routing Priority:</label>
                {isEditing ? (
                  <select
                    name="call_routing_priority"
                    value={formData.call_routing_priority}
                    onChange={handleInputChange}
                  >
                    <option value="web_first">Web First</option>
                    <option value="forwarding_only">Forwarding Only</option>
                    <option value="simultaneous">Simultaneous</option>
                  </select>
                ) : (
                  <span>{user.call_routing_priority || 'simultaneous'}</span>
                )}
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label>Target Number 1:</label>
                  {isEditing ? (
                    <input
                      type="tel"
                      name="targetnumber1"
                      value={formData.targetnumber1}
                      onChange={handleInputChange}
                      placeholder="+1234567890"
                    />
                  ) : (
                    <span>{user.targetnumber1 || 'Not set'}</span>
                  )}
                  {isEditing && (
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="activetargetnum1Enabled"
                        checked={formData.activetargetnum1Enabled}
                        onChange={handleInputChange}
                      />
                      Enable forwarding
                    </label>
                  )}
                </div>

                <div className="form-field">
                  <label>Target Number 2:</label>
                  {isEditing ? (
                    <input
                      type="tel"
                      name="targetnumber2"
                      value={formData.targetnumber2}
                      onChange={handleInputChange}
                      placeholder="+1234567890"
                    />
                  ) : (
                    <span>{user.targetnumber2 || 'Not set'}</span>
                  )}
                  {isEditing && (
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="activetargetnum2Enabled"
                        checked={formData.activetargetnum2Enabled}
                        onChange={handleInputChange}
                      />
                      Enable forwarding
                    </label>
                  )}
                </div>
              </div>
            </div>

            {/* Additional Settings */}
            <div className="profile-section">
              <h4>Additional Settings</h4>
              <div className="form-field">
                <label>SIM CCID:</label>
                {isEditing ? (
                  <input
                    type="text"
                    name="simccid"
                    value={formData.simccid}
                    onChange={handleInputChange}
                    placeholder="Optional SIM card identifier"
                  />
                ) : (
                  <span>{user.simccid || 'Not set'}</span>
                )}
              </div>
            </div>

            {isEditing && (
              <div className="form-actions">
                <button
                  type="submit"
                  disabled={loading}
                  className="save-btn"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="cancel-btn"
                >
                  Cancel
                </button>
              </div>
            )}
          </form>
        </div>

        {/* Change Password */}
        <div className="profile-section">
          <h4>Change Password</h4>
          {pwSuccess && <div className="pw-success">{pwSuccess}</div>}
          {pwError && <div className="pw-error">{pwError}</div>}
          <form onSubmit={async (e) => {
            e.preventDefault();
            setPwError('');
            setPwSuccess('');
            if (pwData.newPw !== pwData.confirm) {
              setPwError('New passwords do not match');
              return;
            }
            if (pwData.newPw.length < 8) {
              setPwError('New password must be at least 8 characters');
              return;
            }
            setPwLoading(true);
            try {
              const response = await authFetch('/auth0/change_password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  current_password: pwData.current,
                  new_password: pwData.newPw
                })
              });
              const data = await response.json();
              if (response.ok) {
                setPwSuccess('Password changed successfully');
                setPwData({ current: '', newPw: '', confirm: '' });
              } else {
                setPwError(data.error || 'Failed to change password');
              }
            } catch (err) {
              setPwError('Failed to change password');
            } finally {
              setPwLoading(false);
            }
          }}>
            <div className="form-field">
              <label>Current Password:</label>
              <input
                type="password"
                value={pwData.current}
                onChange={(e) => setPwData(prev => ({ ...prev, current: e.target.value }))}
                required
              />
            </div>
            <div className="form-field">
              <label>New Password:</label>
              <input
                type="password"
                value={pwData.newPw}
                onChange={(e) => setPwData(prev => ({ ...prev, newPw: e.target.value }))}
                required
                minLength={8}
              />
            </div>
            <div className="form-field">
              <label>Confirm New Password:</label>
              <input
                type="password"
                value={pwData.confirm}
                onChange={(e) => setPwData(prev => ({ ...prev, confirm: e.target.value }))}
                required
                minLength={8}
              />
            </div>
            <div className="form-actions">
              <button type="submit" disabled={pwLoading} className="save-btn">
                {pwLoading ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          </form>
        </div>

        {/* Connected Accounts */}
        <ConnectedAccounts />

        {/* Activity Information */}
        <div className="profile-section">
          <h4>Activity</h4>
          <div className="profile-field">
            <label>Last Login:</label>
            <span>{user.lastlogin ? new Date(user.lastlogin).toLocaleString() : 'Never'}</span>
          </div>
          <div className="profile-field">
            <label>Last Incoming Call:</label>
            <span>{user.lastincomingcall ? new Date(user.lastincomingcall).toLocaleString() : 'Never'}</span>
          </div>
          <div className="profile-field">
            <label>Account Created:</label>
            <span>{user.created ? new Date(user.created).toLocaleString() : 'Unknown'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const ConnectedAccounts = () => {
  const [googleStatus, setGoogleStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const params = new URLSearchParams(window.location.search);
  const justConnected = params.get('google_connected') === 'true';

  useEffect(() => {
    authFetch('/api/google/status').then(r => r.json()).then(setGoogleStatus).catch(() => {});
  }, []);

  const connectGoogle = () => {
    const token = localStorage.getItem('nf_token') || '';
    window.location.href = '/auth/google/sync?token=' + encodeURIComponent(token);
  };

  const disconnectGoogle = async () => {
    if (!window.confirm('Disconnect Google account?')) return;
    await authFetch('/api/google/disconnect', { method: 'POST' });
    setGoogleStatus({ connected: false });
    setSyncResult(null);
  };

  const triggerSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await authFetch('/api/google/sync', { method: 'POST' });
      setSyncResult(await res.json());
    } catch (e) {
      setSyncResult({ error: 'Sync failed' });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="profile-section">
      <h4>Connected Accounts</h4>
      {justConnected && <div className="connect-success">Google account connected successfully!</div>}
      <div className="connected-account">
        <div className="account-row">
          <div className="account-info">
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <div>
              <strong>Google</strong>
              {googleStatus?.connected ? (
                <span className="account-email">{googleStatus.email}</span>
              ) : (
                <span className="account-status">Calendar + Contacts sync</span>
              )}
            </div>
          </div>
          <div className="account-actions">
            {googleStatus?.connected ? (
              <>
                <button className="btn-sync" onClick={triggerSync} disabled={syncing}>{syncing ? 'Syncing...' : 'Sync Now'}</button>
                <button className="btn-reauth" onClick={connectGoogle} title="Re-authorize to add new scopes (Drive, Gmail)">Re-auth</button>
                <button className="btn-disconnect" onClick={disconnectGoogle}>Disconnect</button>
              </>
            ) : (
              <button className="btn-connect-google" onClick={connectGoogle}>Connect</button>
            )}
          </div>
        </div>
        {googleStatus?.connected && googleStatus.lastSync && (
          <div className="last-sync">Last synced: {new Date(googleStatus.lastSync).toLocaleString()}</div>
        )}
        {syncResult && (
          <div className="sync-result">
            {syncResult.message && <div className="sync-message">{syncResult.message}</div>}
            {syncResult.calendar && (syncResult.calendar.error
              ? <span className="sync-error">Calendar: {syncResult.calendar.reconnect ? 'Needs reconnection' : syncResult.calendar.error}</span>
              : <span>Calendar: {syncResult.calendar.imported} imported, {syncResult.calendar.skipped} skipped</span>
            )}
            {syncResult.contacts && (syncResult.contacts.error
              ? <span className="sync-error">Contacts: {syncResult.contacts.reconnect ? 'Needs reconnection' : syncResult.contacts.error}</span>
              : null
            )}
            {syncResult.error && <span className="sync-error">{syncResult.error}</span>}
            {syncResult.needsReconnect && <button className="btn-reconnect" onClick={connectGoogle}>Reconnect Google</button>}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile;
